import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Archive, ArchiveRestore } from 'lucide-react'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { useDataSource } from '../../../lib/dataSource'
import type { Task, Section } from '../../../lib/db/models'
import { isClosedByArchive } from '../../../lib/db/taskFilters'

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface UseProjectSectionsParams {
  projectId: string | undefined
  tasks: Task[] | undefined
  sections: Section[] | undefined
  segments: RenderSegment[]
  /** Optional optimistic-update sink. When provided, every mutation
   *  here calls it BEFORE the IDB write so the change is visible in
   *  the next paint instead of waiting on the LiveQuery refire. */
  setOptimisticSectionOverride?: (sectionId: string, partial: Partial<Section>) => void
  /** Optional optimistic-update sink for tasks. Used by delete-undo to
   *  re-link tasks to their section without waiting on Dexie. */
  setOptimisticTaskOverride?: (taskId: string, partial: Partial<Task>) => void
}

/** Die offene Rückfrage vor dem Archivieren (#193); `null` = keine. */
export interface PendingArchive {
  sectionId: string
  title: string
  openCount: number
}

/**
 * `count` Ordnungen zwischen zwei Nachbarn, gleichmäßig verteilt. Fehlt ein
 * Nachbar, im Abstand 1 davor bzw. dahinter; fehlen beide, ab 0. Für einen
 * einzelnen Abschnitt ergibt das genau die bisherigen Werte (Mitte, ±1, 0).
 */
function spreadOrders(lo: number | undefined, hi: number | undefined, count: number): number[] {
  const slots = Array.from({ length: count }, (_, i) => i + 1)
  if (lo === undefined && hi === undefined) return slots.map(i => i - 1)
  if (hi === undefined) return slots.map(i => lo! + i)
  if (lo === undefined) return slots.map(i => hi - (count + 1) + i)
  const step = (hi - lo) / (count + 1)
  return slots.map(i => lo + step * i)
}

/** Offene Aufgaben eines Abschnitts — die, die das Archivieren abhakt. */
function openTasksOf(tasks: Task[] | undefined, sectionId: string): Task[] {
  return (tasks ?? []).filter(task => task.sectionId === sectionId && !task.deletedAt && task.status !== 'completed')
}

export function useProjectSections({
  projectId, tasks, sections, segments,
  setOptimisticSectionOverride, setOptimisticTaskOverride,
}: UseProjectSectionsParams) {
  const { t } = useTranslation()
  const source = useDataSource()
  // Newly created section ID — triggers inline edit on mount.
  // Cleared only after useLiveQuery has delivered the section into segments.
  const [autoEditSectionId, setAutoEditSectionId] = useState<string | null>(null)
  const toggleCollapseRef = useRef<((sectionId: string) => void) | null>(null)

  // Clear autoEditSectionId only after the section is visible in segments,
  // ensuring the SectionHeader mounts with autoEdit=true.
  useEffect(() => {
    if (!autoEditSectionId) return undefined
    const sectionVisible = segments.some(seg => seg.section?.id === autoEditSectionId)
    if (!sectionVisible) return undefined
    // Clear next tick — SectionHeader has already mounted with autoEdit=true
    const timer = setTimeout(() => setAutoEditSectionId(null), 0)
    return () => clearTimeout(timer)
  }, [autoEditSectionId, segments])

  /*
    Gepackte Aufgaben je frischem Abschnitt, mit ihrem Platz davor. Bekommt der
    Abschnitt keinen Namen (Escape, leer bestätigt), stellt `handleDeleteSection`
    sie zurück; mit Namen wird der Eintrag vergessen.
  */
  const packedRef = useRef(new Map<string, Array<{ id: string; order: number; sectionId: string | null }>>())

  /**
   * Legt einen Abschnitt an — am Anker oder oben/unten. `packTaskIds` kommen
   * gleich mit hinein: direkt unter den neuen Kopf, in ihrer Reihenfolge
   * (⇧A/⇧B/⇧F oder Pille mit mehreren markierten Aufgaben).
   *
   * `onCreated` meldet die Kennung, sobald der Abschnitt geschrieben ist — noch bevor die Liste ihn zeigt
   * (der Abschnitts-Zug aus der Pille wartet darauf, #314).
   */
  const handleAddSection = useCallback(async (
    position: 'top' | 'bottom' = 'bottom',
    anchorId?: string,
    placement?: 'before' | 'after',
    packTaskIds: string[] = [],
    onCreated?: (sectionId: string) => void,
  ) => {
    if (!projectId) return

    const packing = new Set(packTaskIds)
    const packed = (tasks ?? []).filter(t => packing.has(t.id)).sort((a, b) => a.order - b.order)
    // Die gepackten Aufgaben verlassen ihren Platz — sie zählen nicht als Nachbarn.
    const allItems = [
      ...(tasks ?? []).filter(t => !packing.has(t.id)).map(t => ({ id: t.id, order: t.order })),
      ...(sections ?? []).map(s => ({ id: s.id, order: s.order })),
    ].sort((a, b) => a.order - b.order)

    const anchorIdx = anchorId && placement ? allItems.findIndex(item => item.id === anchorId) : -1
    let prev: { order: number } | undefined
    let next: { order: number } | undefined
    if (anchorIdx !== -1) {
      prev = placement === 'before' ? allItems[anchorIdx - 1] : allItems[anchorIdx]
      next = placement === 'before' ? allItems[anchorIdx] : allItems[anchorIdx + 1]
    } else if (position === 'top') {
      next = allItems[0]
    } else {
      prev = allItems[allItems.length - 1]
    }

    const [order, ...taskOrders] = spreadOrders(prev?.order, next?.order, 1 + packed.length)
    const newSection = await source.createSection({
      title: '',
      projectId,
      order,
    })
    onCreated?.(newSection.id)
    // Gleich, nicht erst nach dem Umsortieren: Ab hier zählt er als frischer Abschnitt (die Pille bleibt unten).
    setAutoEditSectionId(newSection.id)
    if (packed.length > 0) {
      packedRef.current.set(newSection.id, packed.map(t => ({ id: t.id, order: t.order, sectionId: t.sectionId ?? null })))
      const moves = packed.map((t, i) => ({ id: t.id, order: taskOrders[i], sectionId: newSection.id }))
      for (const move of moves) setOptimisticTaskOverride?.(move.id, { order: move.order, sectionId: move.sectionId })
      await source.reorderTasks(moves)
    }
  }, [projectId, tasks, sections, source, setOptimisticTaskOverride])

  const handleToggleCollapse = useCallback(async (sectionId: string) => {
    // Look up the current state from the in-memory list to avoid an
    // extra storage roundtrip — the optimistic flip needs to fire BEFORE
    // any await so the next paint shows the new collapsed state.
    const sectionsNow = sections ?? []
    const current = sectionsNow.find(s => s.id === sectionId)
    if (!current) {
      // Fall back to the slower but always-correct path.
      const section = await source.getSection(sectionId)
      if (!section) return
      setOptimisticSectionOverride?.(sectionId, { collapsed: !section.collapsed })
      await source.updateSection(sectionId, { collapsed: !section.collapsed })
      return
    }
    setOptimisticSectionOverride?.(sectionId, { collapsed: !current.collapsed })
    await source.updateSection(sectionId, { collapsed: !current.collapsed })
  }, [sections, setOptimisticSectionOverride, source])
  toggleCollapseRef.current = handleToggleCollapse

  const handleRenameSection = useCallback(async (sectionId: string, title: string) => {
    // Benannt: Das Packen gilt, ein späteres Löschen lässt die Aufgaben stehen.
    packedRef.current.delete(sectionId)
    setOptimisticSectionOverride?.(sectionId, { title })
    await source.updateSection(sectionId, { title })
  }, [setOptimisticSectionOverride, source])

  const handleDeleteSection = useCallback(async (sectionId: string) => {
    // Snapshot the section for undo; the delete reports the tasks it unlinked.
    const section = await source.getSection(sectionId)

    // Ein frisch gepackter Abschnitt ohne Namen: erst die Aufgaben an ihren
    // alten Platz, dann geht er wie jeder leere — Escape nimmt alles zurück.
    const packedBefore = packedRef.current.get(sectionId)
    if (packedBefore) {
      packedRef.current.delete(sectionId)
      if (!section?.title) {
        for (const place of packedBefore) setOptimisticTaskOverride?.(place.id, { order: place.order, sectionId: place.sectionId })
        await source.reorderTasks(packedBefore)
      }
    }

    // Optimistic delete: drops the section out of the rendered segments
    // (the page filter excludes sections with `deletedAt` set).
    setOptimisticSectionOverride?.(sectionId, { deletedAt: new Date() })

    const { taskIds: affectedTaskIds } = await source.deleteSection(sectionId)

    // Show undo toast only for named sections (unnamed = auto-cleanup, no toast)
    if (section?.title) {
      showUndoToast({
        message: t('section.deleted', { title: section.title }),
        undoLabel: t('common.undo'),
        undoneMessage: t('undo.restored.section', { title: section.title }),
        onUndo: async () => {
          // Optimistic restore: clear `deletedAt` on the section and
          // re-link the affected tasks. The writes still run immediately
          // after, but the UI already reflects the undo.
          setOptimisticSectionOverride?.(section.id, { deletedAt: null })
          for (const taskId of affectedTaskIds) {
            setOptimisticTaskOverride?.(taskId, { sectionId })
          }
          await source.restoreSection(section, affectedTaskIds)
        },
      })
    }

  }, [t, setOptimisticSectionOverride, setOptimisticTaskOverride, source])

  // ── Archivieren (#193) ──
  const [pendingArchive, setPendingArchive] = useState<PendingArchive | null>(null)

  /*
    Hakt die offenen Aufgaben ab und blendet den Abschnitt aus — optimistisch, sonst stünde er
    bis zum Rücklauf aus dem Speicher mit der Marke „Archiviert“ in der Liste (Offenes hält ihn
    dort). Rückgängig öffnet genau die Aufgaben wieder, die DIESES Archivieren abgehakt hat;
    „Wiederherstellen“ später lässt sie erledigt.
  */
  const archiveNow = useCallback(async (sectionId: string) => {
    const section = sections?.find(s => s.id === sectionId) ?? await source.getSection(sectionId)
    if (!section) return
    const now = new Date()
    setOptimisticSectionOverride?.(sectionId, { archivedAt: now })
    for (const task of openTasksOf(tasks, sectionId)) {
      setOptimisticTaskOverride?.(task.id, { status: 'completed', completedAt: now })
    }
    const { completedTaskIds } = await source.archiveSection(sectionId)
    const title = section.title || t('section.defaultName')
    showUndoToast({
      message: t('section.archived', { title }),
      // Was das Archivieren tat (#223): abgehakt, oder wohin er ging.
      description: completedTaskIds.length > 0
        ? t('section.archivedClosed', { count: completedTaskIds.length })
        : t('section.archivedText'),
      undoLabel: t('common.undo'),
      undoneMessage: t('undo.restored.section', { title }),
      icon: createElement(Archive, { size: 18 }),
      tone: 'archive',
      onUndo: async () => {
        setOptimisticSectionOverride?.(sectionId, { archivedAt: null })
        for (const taskId of completedTaskIds) {
          setOptimisticTaskOverride?.(taskId, { status: 'todo', completedAt: null })
        }
        await source.unarchiveSection(sectionId, completedTaskIds)
      },
    })
  }, [sections, tasks, source, t, setOptimisticSectionOverride, setOptimisticTaskOverride])

  /** Mit offenen Aufgaben erst die Rückfrage, sonst sofort. */
  const handleArchiveSection = useCallback((sectionId: string) => {
    const openCount = openTasksOf(tasks, sectionId).length
    if (openCount === 0) {
      void archiveNow(sectionId)
      return
    }
    const title = sections?.find(s => s.id === sectionId)?.title || t('section.defaultName')
    setPendingArchive({ sectionId, title, openCount })
  }, [tasks, sections, archiveNow, t])

  const confirmArchive = useCallback(() => {
    if (!pendingArchive) return
    setPendingArchive(null)
    void archiveNow(pendingArchive.sectionId)
  }, [pendingArchive, archiveNow])

  const cancelArchive = useCallback(() => setPendingArchive(null), [])

  /*
    **Wiederherstellen** — nur den Abschnitt, oder `withTasks`: dazu die Aufgaben, die das
    Archivieren abgehakt hat (`isClosedByArchive`); was vorher erledigt war, bleibt erledigt.
  */
  const handleUnarchiveSection = useCallback(async (sectionId: string, withTasks = false) => {
    const section = sections?.find(s => s.id === sectionId)
    setOptimisticSectionOverride?.(sectionId, { archivedAt: null })
    if (withTasks && section) {
      for (const task of (tasks ?? []).filter(task => isClosedByArchive(task, section))) {
        setOptimisticTaskOverride?.(task.id, { status: 'todo', completedAt: null })
      }
    }
    await source.unarchiveSection(sectionId, withTasks ? 'closedByArchive' : [])
    const title = section?.title || t('section.defaultName')
    showSuccessToast({
      message: t('section.unarchived', { title }),
      description: withTasks ? t('section.unarchivedTasksText') : t('section.unarchivedText'),
      icon: createElement(ArchiveRestore, { size: 18 }),
      tone: 'success',
    })
  }, [sections, tasks, source, t, setOptimisticSectionOverride, setOptimisticTaskOverride])

  return {
    autoEditSectionId,
    handleAddSection,
    handleToggleCollapse,
    handleRenameSection,
    handleDeleteSection,
    handleArchiveSection,
    handleUnarchiveSection,
    pendingArchive,
    confirmArchive,
    cancelArchive,
    toggleCollapseRef,
  }
}
